import { Component, OnInit, OnDestroy, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { NgZone } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, takeUntil, timeout } from 'rxjs';
import { OnboardingService } from '../onboarding.service';
import { InternalAnalyticsService } from '../../core/internal-analytics.service';
import { ScriptLoaderService } from '../../core/helpers/script-loader.service';
import { UserService } from '../../core/user/user.service';
import { DataStorage } from '../../core/data/data-storage';

declare const Stripe: any;

/**
 * Self-contained Variant B card step. Used both in the email signup flow
 * (RegisterComponent) and for SSO signups whose business already exists.
 * Loads a SetupIntent, mounts Stripe Elements with an instant overlay
 * placeholder (no flicker), and on success stores the payment method.
 * Emits `completed` once the card is stored or when the experiment is
 * disabled server-side (so the caller can advance the flow either way).
 */
@Component({
  selector: 'rosie-registration-card',
  templateUrl: './registration-card.component.html',
  styleUrls: ['./registration-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class RegistrationCardComponent implements OnInit, OnDestroy {
  @Output() completed = new EventEmitter<void>();

  cardForm: UntypedFormGroup;
  promoState: 'collapsed' | 'expanded' | 'applied' = 'collapsed';
  promoInProgress = false;
  promoError = '';
  appliedPromoName = '';
  cardError = '';
  cardInProgress = false;

  // Card-brand badges (Variant D). All four show until Stripe detects one, then the
  // rest animate away (and slide back if the number is cleared). Empty = show all.
  cardBrand = '';
  readonly brands = ['visa', 'mastercard', 'amex', 'discover'];

  // iOS WebKit natively paints a grey tap rectangle over cross-origin iframes;
  // no CSS on the iframe removes it. While a field is unfocused, a transparent
  // shield covers its iframe so the tap lands on our own element (no native
  // highlight) and the wrapper's click handler focuses Stripe programmatically;
  // the shield lifts while focused so caret taps reach the iframe normally.
  cardNumberFocused = false;
  cardExpiryFocused = false;
  cardCvcFocused = false;

  // The Stripe iframes arrive well after our own DOM (SetupIntent fetch + script
  // load + iframe boot), so their placeholders visibly pop in late. Until each
  // element reports ready, the template paints an identical placeholder in our
  // own DOM — same text, font and colour, so the handoff is invisible.
  cardNumberReady = false;
  cardExpiryReady = false;
  cardCvcReady = false;

  // Gates field-level validation so nothing shows on a bare focus/blur (only
  // after the user types — dirty — or attempts to submit).
  submitAttempted = false;

  private persistentStorage = DataStorage.persistent();
  private registrationCredentials: { publishableKey?: string, clientSecret?: string } = {};
  private readonly stripeScript = { name: 'stripe', src: 'https://js.stripe.com/v3/' };
  private stripe: any;
  private stripeElements: any;
  private cardNumberElement: any;
  private cardExpiryElement: any;
  private cardCvcElement: any;
  private cardNumberComplete = false;
  private cardExpiryComplete = false;
  private cardCvcComplete = false;
  private appliedPromoCode = '';

  private destroy = new Subject<void>();

  constructor(private formBuilder: UntypedFormBuilder, private onboarding: OnboardingService,
    private analytics: InternalAnalyticsService, private sanitizer: DomSanitizer,
    private translate: TranslateService, private scriptLoader: ScriptLoaderService, private zone: NgZone,
    private userService: UserService) {
    this.cardForm = this.formBuilder.group({
      zip: ['', [Validators.required]],
      promoCode: ['']
    });
  }

  ngOnInit() {
    this.preloadStripe();
    this.initCardForm();
    this.autoApplyStoredCoupon();
    this.fireLeadOnReachingCard();
  }

  // Variant D: CompleteRegistration fires at card-add — instead of at the Customize
  // step, which D users only reach after training — and it must carry the
  // deterministic event_id (md5 of the user uuid) so Meta dedupes it against the
  // backend CAPI copy fired off the same CardAdded event. The card step runs before
  // /user has ever been fetched, so load it first; fire regardless on error (an
  // id-less pixel still counts, it just can't dedupe). Deliberately no
  // takeUntil(destroy): navigation unmounts this component right after
  // completed.emit(), and cancelling would drop the fire. The marker tells
  // configuration-step to consume its fireInitialTracking flag without re-firing.
  private fireCompleteRegistrationForVariantD() {
    if (!this.isVariantD) {
      return;
    }

    this.persistentStorage.set('initialTrackingFired', true);

    if (this.userService.id) {
      this.analytics.fireFirstTimeUserVisit();
      return;
    }

    this.userService.getUser().pipe(timeout({ first: 5000 })).subscribe({
      next: () => this.analytics.fireFirstTimeUserVisit(),
      error: () => this.analytics.fireFirstTimeUserVisit()
    });
  }

  // Variant D has no agent-preview page (where A/B fire their Lead pixel), so the
  // card screen is the lead moment: the account exists and intent is shown. Once-
  // guarded because a refresh resumes on the card step — Facebook dedupes via the
  // deterministic event_id, but Bing has no dedupe.
  private fireLeadOnReachingCard() {
    if (!this.isVariantD || this.persistentStorage.get('registrationLeadFired')) {
      return;
    }

    this.persistentStorage.set('registrationLeadFired', true);
    this.analytics.fireLeadEvent();
  }

  // A coupon captured from the signup URL (?coupon=, stashed as signupCouponCode)
  // should show as applied on the card step so the discount is visible — it mirrors
  // the coupon the backend already attaches from user.meta at card-add. A manually
  // typed promo still overrides it; an invalid stored coupon fails silently.
  private autoApplyStoredCoupon() {
    const stored = (this.analytics.coupon || '').trim();

    if (!stored || this.promoState !== 'collapsed') {
      return;
    }

    this.cardForm.patchValue({ promoCode: stored });
    this.promoState = 'expanded';
    this.applyPromo(true);
  }

  ngOnDestroy() {
    this.destroy.next();
    this.destroy.complete();
    this.cardNumberElement?.unmount();
    this.cardExpiryElement?.unmount();
    this.cardCvcElement?.unmount();
  }

  get isVariantD(): boolean {
    return this.onboarding.experimentVariant === 'D';
  }

  // Stripe iframes only occupy the text line, so taps on the field's padding miss
  // them and the keyboard doesn't open (mobile felt "buggy to click into"). Focus
  // the element on any tap of its wrapper.
  focusCard() { this.cardNumberElement?.focus(); }
  focusExpiry() { this.cardExpiryElement?.focus(); }
  focusCvc() { this.cardCvcElement?.focus(); }

  getTOSAndPP(): any {
    return this.sanitizer.bypassSecurityTrustHtml(this.translate.instant('REGISTER.PP_TOS'));
  }

  expandPromo() {
    this.promoState = 'expanded';
  }

  applyPromo(silent = false) {
    if (this.promoInProgress) {
      return;
    }

    const code = (this.cardForm.value.promoCode || '').trim();

    if (!code) {
      return;
    }

    this.promoInProgress = true;
    this.promoError = '';

    this.onboarding.validateRegistrationCoupon(code)
      .pipe(takeUntil(this.destroy))
      .subscribe((coupon) => {
        this.appliedPromoCode = coupon?.code || code;
        this.appliedPromoName = coupon?.name || code;
        this.promoState = 'applied';
        this.promoInProgress = false;
      }, (error) => {
        this.promoInProgress = false;

        // Auto-applied (URL) coupons fail quietly — collapse rather than surfacing
        // an error the user never typed.
        if (silent) {
          this.removePromo();
          return;
        }

        this.promoError = (typeof error === 'string' ? error : null) || 'Invalid Code';
      });
  }

  removePromo() {
    this.appliedPromoCode = '';
    this.appliedPromoName = '';
    this.promoError = '';
    this.cardForm.get('promoCode')?.reset();
    this.promoState = 'collapsed';
  }

  submitCard() {
    this.submitAttempted = true;
    this.cardForm.markAllAsTouched();

    if (this.cardInProgress || !this.stripe || !this.cardNumberElement) {
      return;
    }

    if (!this.cardComplete || this.cardForm.invalid) {
      this.cardError = 'Add a credit card above to create your Rosie account and start the free trial.';
      return;
    }

    this.cardInProgress = true;
    this.cardError = '';

    this.stripe.confirmCardSetup(this.registrationCredentials.clientSecret, {
      payment_method: {
        card: this.cardNumberElement,
        billing_details: { address: { postal_code: this.cardForm.value.zip } }
      }
    }).then((result: any) => {
      if (result.error) {
        this.cardError = result.error?.code === 'card_declined'
          ? 'We were unable to add your card. Please note that prepaid cards are not supported. If your card is not prepaid, ensure the ZIP code and CVC are entered correctly.'
          : (result.error?.message || 'An error occurred. Please try again.');
        this.cardInProgress = false;
        return;
      }

      this.onboarding.storeRegistrationPaymentMethod(result.setupIntent.payment_method, this.appliedPromoCode || undefined)
        .pipe(takeUntil(this.destroy))
        .subscribe(() => {
          this.fireCompleteRegistrationForVariantD();
          // Keep the button in progress: completed -> the caller refreshes the user
          // and navigates, which unmounts this component. Clearing it here would
          // leave a dead, idle button during that refresh window and invite a
          // re-click (which is what made the card step look stuck).
          this.completed.emit();
        }, () => {
          this.cardInProgress = false;
          this.cardError = 'Something went wrong saving your card. Please try again.';
        });
    });
  }

  private initCardForm() {
    this.onboarding.getRegistrationBilling()
      .pipe(takeUntil(this.destroy))
      .subscribe((credentials) => {
        if (!credentials?.publishableKey || !credentials?.clientSecret) {
          // Experiment disabled server-side; let the caller advance the flow.
          this.completed.emit();
          return;
        }
        this.registrationCredentials = credentials;
        this.loadStripeAndMount();
      }, () => this.completed.emit());
  }

  private preloadStripe() {
    if (typeof Stripe !== 'undefined') {
      return;
    }

    this.scriptLoader.load(this.stripeScript).subscribe({ next: () => { }, error: () => { } });
  }

  private loadStripeAndMount() {
    this.preloadStripe();

    this.whenStripeReady()
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        this.stripe = Stripe(this.registrationCredentials.publishableKey);
        this.stripeElements = this.stripe.elements({
          clientSecret: this.registrationCredentials.clientSecret,
          fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500&display=swap' }]
        });
        setTimeout(() => this.mountCardFields(), 0);
      }, () => this.cardError = 'Unable to load the payment form. Please refresh the page and try again.');
  }

  private whenStripeReady(): Observable<void> {
    return new Observable<void>((observer) => {
      const start = Date.now();
      const poll = () => {
        if (typeof Stripe !== 'undefined') {
          observer.next();
          observer.complete();
          return;
        }

        if (Date.now() - start > 10000) {
          observer.error('Stripe.js failed to load');
          return;
        }

        setTimeout(poll, 50);
      };

      poll();
    });
  }

  private mountCardFields() {
    // Grey placeholders for Variant D (Figma), near-black for the Variant B card.
    const placeholderColor = this.isVariantD ? '#9487a0' : '#221f20';
    const style = {
      base: {
        fontFamily: 'Outfit, sans-serif',
        fontSize: '14px',
        fontWeight: '400',
        color: '#221f20',
        '::placeholder': { color: placeholderColor, fontWeight: '400' }
      }
    };
    // No native icon: Stripe's individual cardNumber element only renders it on the
    // left. We render the brand badges on the right ourselves (Figma position + the
    // graceful reduce-to-one from Jessica's reference), driven by Stripe's brand
    // detection below. Native placeholders are kept.
    this.cardNumberElement = this.stripeElements.create('cardNumber', { style, placeholder: 'Card number' });
    this.cardExpiryElement = this.stripeElements.create('cardExpiry', { style, placeholder: 'MM / YY' });
    this.cardCvcElement = this.stripeElements.create('cardCvc', { style, placeholder: 'CVC' });

    this.cardNumberElement.mount('#reg-card-number');
    this.cardExpiryElement.mount('#reg-card-expiry');
    this.cardCvcElement.mount('#reg-card-cvc');

    this.cardNumberElement.on('change', (event: any) => {
      // Stripe events fire outside Angular's zone — run in-zone so error/complete
      // + the detected brand re-render.
      this.zone.run(() => {
        this.cardNumberComplete = event.complete;
        this.cardError = event.error?.message || '';
        this.cardBrand = this.brands.includes(event.brand) ? event.brand : '';
      });
    });
    this.cardExpiryElement.on('change', (event: any) => this.cardExpiryComplete = event.complete);
    this.cardCvcElement.on('change', (event: any) => this.cardCvcComplete = event.complete);

    this.trackFocus(this.cardNumberElement, (focused) => this.cardNumberFocused = focused);
    this.trackFocus(this.cardExpiryElement, (focused) => this.cardExpiryFocused = focused);
    this.trackFocus(this.cardCvcElement, (focused) => this.cardCvcFocused = focused);

    this.cardNumberElement.on('ready', () => this.zone.run(() => this.cardNumberReady = true));
    this.cardExpiryElement.on('ready', () => this.zone.run(() => this.cardExpiryReady = true));
    this.cardCvcElement.on('ready', () => this.zone.run(() => this.cardCvcReady = true));
  }

  // Drives the tap shields (see template): each Stripe field is covered by a
  // transparent overlay while unfocused, and uncovered while focused.
  private trackFocus(element: any, setFocused: (focused: boolean) => void) {
    element.on('focus', () => this.zone.run(() => setFocused(true)));
    element.on('blur', () => this.zone.run(() => setFocused(false)));
  }

  private get cardComplete(): boolean {
    return this.cardNumberComplete && this.cardExpiryComplete && this.cardCvcComplete;
  }
}
